// Grid and Snapping
export const GRID_SIZE = 30;
export const SNAP_THRESHOLD = 3; // 3cm maximum snap distance (3 pixels)
export const POINT_SNAP_THRESHOLD = 3; // 3cm snap area for precise control
export const SNAP_DISTANCE = 3; // 3cm distance in pixels for snapping points
export const WALL_SNAP_THRESHOLD = 3; // 3cm distance to snap walls to other walls

// Measurements and Scale
export const PIXELS_PER_METER = 100;
export const CM_PER_METER = 100;
export const CM_SNAP_SIZE = PIXELS_PER_METER / CM_PER_METER; // 0.3 pixels per cm
export const CANVAS_SIZE = PIXELS_PER_METER * 100; // 3000 pixels (100 meters)

// Wall and Element Appearance
export const WALL_THICKNESS = 5;
export const RADIATOR_WALL_OFFSET = 13; // Increased to improve clearance; matches boiler effective gap (BOILER_WALL_OFFSET 18 minus radiator depth/2 5)
export const BOILER_WALL_OFFSET = 18; // Increased to move H/S further from wall
export const GRID_COLOR = '#E5E7EB';

// Base font sizes (used for calculations)
export const BASE_ROOM_LABEL_FONT_SIZE = 16;
export const BASE_MEASUREMENT_FONT_SIZE = 14;
export const BASE_TEXT_BOX_FONT_SIZE = 12;

// Font scaling configuration
export const FONT_SCALE_FACTOR = 0.8; // How much text scales inversely with zoom
export const MIN_FONT_SCALE = 0.5; // Minimum scaling factor
export const MAX_FONT_SCALE = 3.0; // Maximum scaling factor

// Zoom-responsive font calculation
export const calculateZoomResponsiveFontSize = (baseFontSize: number, scale: number): number => {
  // Inverse relationship: when scale is low (zoomed out), font gets bigger
  // when scale is high (zoomed in), font gets smaller
  const scaleFactor = Math.pow(1 / scale, FONT_SCALE_FACTOR);
  const clampedScaleFactor = Math.max(MIN_FONT_SCALE, Math.min(MAX_FONT_SCALE, scaleFactor));
  return Math.round(baseFontSize * clampedScaleFactor);
};

// Zoom-responsive font functions
export const getZoomResponsiveRoomLabelFont = (scale: number): string => {
  const fontSize = calculateZoomResponsiveFontSize(BASE_ROOM_LABEL_FONT_SIZE, scale);
  return `${fontSize}px Inter`;
};

export const getZoomResponsiveMeasurementFont = (scale: number): string => {
  const fontSize = calculateZoomResponsiveFontSize(BASE_MEASUREMENT_FONT_SIZE, scale);
  return `${fontSize}px Inter`;
};

export const getZoomResponsiveTextBoxFont = (scale: number): string => {
  const fontSize = calculateZoomResponsiveFontSize(BASE_TEXT_BOX_FONT_SIZE, scale);
  return `${fontSize}px Inter`;
};

// Legacy constants (for backward compatibility, but recommend using zoom-responsive versions)
export const ROOM_LABEL_FONT = '14px Inter';
export const MEASUREMENT_FONT = '12px Inter';
export const RESIZE_HANDLE_SIZE = 0; // Visual size of the handle
export const RESIZE_HANDLE_HOVER_SIZE = 12; // Clickable/hoverable area for resize handles

// Zoom and Viewport
export const SCALE_FACTOR = 1.1;

// Touch Interaction Thresholds (Optimized for responsiveness)
export const TOUCH_DRAG_THRESHOLD = 2; // Reduced from 5 for immediate responsiveness
export const TOUCH_DOUBLE_TAP_DELAY = 250;
export const TOUCH_LONG_PRESS_DELAY = 200; // Reduced from 400ms for faster response
export const MIN_PINCH_SCALE_DELTA = 0.01;

// iOS Specific (Keep only used ones if needed)
export const IOS17_TOUCH_DELAY = 10;
export const IOS17_MOMENTUM_DECAY = 0.92;
export const IOS17_PINCH_SENSITIVITY = 1.2;
export const IOS17_DOUBLE_TAP_THRESHOLD = 200;
export const TOUCH_MOMENTUM_DURATION = 300;
export const TOUCH_MOMENTUM_FACTOR = 0.95;

// Helper Function: Get Container Size for Initial Scale Calculation
const getContainerSizeForScale = () => {
  const windowWidth = typeof window !== 'undefined' ? window.innerWidth : 1200;
  if (windowWidth < 768) {
    return windowWidth - 32;
  }
  return Math.min(windowWidth * 0.9, 1200);
};
export const DEFAULT_CONTAINER_SIZE_FOR_SCALE = getContainerSizeForScale();

export const calculateInitialScale = (size = DEFAULT_CONTAINER_SIZE_FOR_SCALE) => {
  const baseScale = size / CANVAS_SIZE;
  return baseScale * 4;
};
export const INITIAL_SCALE = 1;
export const MAX_SCALE = 5;
export const MIN_SCALE = 0.1; // Define and export MIN_SCALE

// Function to create eraser cursor (Needs DOM access, keep here or move to a UI util)
export const createEraserCursor = (size = 32, color = '#3B82F6') => {
  if (typeof window === 'undefined' || typeof btoa === 'undefined') return 'crosshair'; // Fallback for SSR or environments without btoa
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
      <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 2}" fill="${color}" fill-opacity="0.9"/>
      <circle cx="${size / 2}" cy="${size / 2}" r="3" fill="white"/>
    </svg>
  `;
  try {
      const dataUrl = `data:image/svg+xml;base64,${btoa(svg)}`;
      return `url(${dataUrl}) ${size / 2} ${size / 2}, auto`;
  } catch (e) {
      console.error("Error creating eraser cursor:", e);
      return 'crosshair'; // Fallback on error
  }
};

// Grid constants
export const GRID_SPACING_METERS = 1; // 1 meter
export const SUBGRID_DIVISIONS = 2; // e.g., 2 for 0.5m subgrid lines

// Other constants
export const DEFAULT_FONT_SIZE = 14;
export const COMPASS_DEFAULT_SIZE = 50;

export const SNAP_ANGLE_TOLERANCE_PX = 10; // Pixels on screen for angle snapping

export const WALL_RESIZE_HOVER_TOLERANCE = 40; // Increased from 20 (or previous value)
export const WALL_RESIZE_TOUCH_TOLERANCE = 100; // Very large touch area for easy first-touch selection
export const INITIAL_TOUCH_SELECTION_RADIUS = 120; // Extra large area for first touch detection 

export const A4_WIDTH = 210; // Example value in mm
export const A4_HEIGHT = 297; // Example value in mm
export const MARGIN = 10; // Example value in mm 

export const POINT_MATCH_TOLERANCE = 0.5; // Increased for robustness in endpoint matching

export const HOVER_TOLERANCE = 10; // General hover tolerance in screen pixels

// Click and interaction tolerances (screen-space pixels for consistent UX regardless of zoom)
export const MIN_CLICK_TOLERANCE = 12; // Minimum tolerance for wall/element clicking (pixels on screen)
export const MIN_INTERACTION_TOLERANCE = 15; // Minimum tolerance for interactive elements like radiators, windows, etc.

export const ROOM_AUTOLABEL_AREA_THRESHOLD_SQ_METERS = 1; // Or your desired threshold
